import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { motion } from "framer-motion";
import { faLoader } from "@insidebeehive/fortawesome/pro-solid-svg-icons/faLoader";

import { Icon } from "./icon";

import type { IconProp } from "@insidebeehive/fortawesome/fontawesome-svg-core";
import type { MotionProps } from "framer-motion";
import type { colors } from "../helper/types";
import type { IconType } from "./icon";

import { cn } from "~/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center whitespace-nowrap text-sm font-medium shadow-sm ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90",
        destructive: "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline: "border border-input bg-background hover:bg-accent hover:text-accent-foreground",
        soft: "text-black-foreground hover:bg-indigo/90 bg-primary-foreground",
        secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
        info: "bg-blue-500 text-white hover:bg-blue-600",
      },
      size: {
        xs: "px-2 py-1 text-xs",
        sm: "px-2 py-1 text-sm",
        default: "px-2.5 py-1.5 text-sm",
        lg: "px-3 py-2 text-sm",
        xl: "px-3.5 py-2.5 text-sm",
        icon: "h-10 w-10",
        smallIcon: "h-6 w-6",
      },
      rounded: {
        sm: "rounded-sm",
        md: "rounded-md",
        lg: "rounded-lg",
        xl: "rounded-xl",
        none: "rounded-none",
        full: "rounded-full",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
      rounded: "md",
    },
  },
);

// handlers that DOM and framer-motion type differently; the motion versions win
type MotionConflicts = "onAnimationStart" | "onDrag" | "onDragStart" | "onDragEnd";

export interface ButtonProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, MotionConflicts>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  icon?: IconProp;
  iconName?: IconType;
  alignIcon?: "left" | "right";
  iconStyle?: string;
  full?: boolean;
  isLoading?: boolean;
  backgroundColor?: colors;
  noAnimation?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps & MotionProps>(({ className, variant, size, backgroundColor, asChild = false, icon, iconName, alignIcon, iconStyle, rounded, full, isLoading, noAnimation, ...props }, ref) => {
  const Comp = asChild ? Slot : "button";
  const bgColor = (color?: colors) => (color ? `bg-${color}-100 text-${color}-600 hover:bg-${color}-50 hover:text-${color}-600` : "");

  return (
    <motion.button
      whileTap={!isLoading && !noAnimation ? (props?.whileTap ? props?.whileTap : { scale: 0.98, outline: "none", border: "none" }) : undefined}
      className={cn(buttonVariants({ variant, size, className: `${className} ${bgColor(backgroundColor)}`, rounded }), full && "w-full")}
      disabled={isLoading}
      ref={ref}
      {...(props as MotionProps)}
    >
      {icon ? (
        <Icon className={`${alignIcon === "right" ? "order-2 ml-2" : "mr-2"} ${iconStyle} ${isLoading ? "animate-spin" : ""}`} icon={isLoading ? faLoader : icon} name={isLoading ? "loader-solid" : iconName} />
      ) : (
        isLoading && <Icon icon={faLoader} spin name="loader-solid" className="mr-2" />
      )}
      {isLoading ? "Please wait..." : props.children}
    </motion.button>
  );
});
Button.displayName = "Button";

const iconButtonVariants = cva("border-borderColor p-1.5 rounded-md", {
  variants: {
    size: {
      xs: "h-2 w-2",
      sm: "h-3 w-3",
      default: "h-4 w-4",
      lg: "h-5 w-5",
    },
  },
  defaultVariants: {
    size: "default",
  },
});
export interface IconButtonProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, MotionConflicts>, VariantProps<typeof iconButtonVariants> {
  asChild?: boolean;
  icon?: IconProp;
  iconName?: string;
  alignIcon?: "left" | "right";
  iconStyle?: string;
  full?: boolean;
  color?: colors;
  avoidAnimate?: boolean;
}

const IconButton = React.forwardRef<HTMLButtonElement, IconButtonProps & MotionProps>(({ avoidAnimate, className, size = "default", icon, iconName, iconStyle, color, ...props }, ref) => {
  const iconColor = (color?: colors) => `text-${color}-500`;

  return (
    <motion.button whileTap={avoidAnimate ? undefined : props?.whileTap ? props?.whileTap : { scale: 0.98, outline: "none", border: "none" }} ref={ref} {...(props as MotionProps)}>
      {icon && <Icon className={cn(iconButtonVariants({ size }), iconColor(color), className, "transition-all duration-200")} icon={icon} name={iconName} />}
    </motion.button>
  );
});
IconButton.displayName = "IconButton";

export { Button, IconButton, buttonVariants };
